/**
 * ConnectivityProfile — Shows the full digital infrastructure available to a
 * business premises. Used in the map popup, BusinessProfile page and search-
 * result cards so users always see the borough-wide baseline (Gigabit, 4G/5G,
 * USO) in addition to whatever business-specific values we have on file.
 *
 * Strategy:
 * - Borough baseline (Ofcom Connected Nations July 2025) is fetched once and
 *   memoised. Every premises in Stockton has AT LEAST these.
 * - If the business has stronger specifics (e.g. ofcom_gigabit_pct=100, FTTP
 *   present, download_speed) we surface them inline alongside the baseline.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { Wifi, Smartphone, Radio, Signal, ShieldCheck } from 'lucide-react';
import api from '../lib/api';

const FALLBACK = {
  gigabit_pct: 95.0,        // Gigabit-capable (any tech)
  fttp_pct: 59.2,           // Full-fibre to the premises
  fwa_pct: 26.2,            // Fixed Wireless Access
  ultrafast_pct: 95.0,
  fourg_outdoor_pct: 99.9,
  fourg_indoor_pct: 89.33,
  fiveg_outdoor_pct: 71.25,
  below_uso_pct: 0.0,
  source: 'Ofcom Connected Nations, July 2025',
};

const Ctx = createContext(FALLBACK);

export function ConnectivityBaselineProvider({ children }) {
  const [baseline, setBaseline] = useState(FALLBACK);
  const laScope = typeof window !== 'undefined' ? localStorage.getItem('adoptic_la_scope') || 'tvca' : 'tvca';
  useEffect(() => {
    api.get('/analytics/connectivity-baseline')
      .then(({ data }) => { if (data && typeof data === 'object') setBaseline({ ...FALLBACK, ...data }); })
      .catch(() => {});
  }, [laScope]);
  return <Ctx.Provider value={baseline}>{children}</Ctx.Provider>;
}

export const useConnectivityBaseline = () => useContext(Ctx);

const fmtPct = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (v === 0) return '0%';
  if (v >= 99.95) return '100%';
  if (Number.isInteger(v)) return `${v}%`;
  return `${Number(v).toFixed(v < 10 ? 1 : 2).replace(/\.?0+$/, '')}%`;
};

const dot = (pct) =>
  pct == null ? 'bg-ink/20'
  : pct >= 95 ? 'bg-emerald-500'
  : pct >= 80 ? 'bg-emerald-400'
  : pct >= 50 ? 'bg-amber-500'
  : 'bg-red-500';

/**
 * Compact one-row summary for use in tight tooltips (map popup, list cards).
 * Shows 5 metrics as label · % chips on a single horizontal scroll row.
 */
export function ConnectivityProfileCompact({ business, className = '' }) {
  const b = useConnectivityBaseline();
  const gig = business?.ofcom_gigabit_pct ?? b.gigabit_pct;
  const uso = business?.ofcom_below_uso_pct ?? b.below_uso_pct;

  const items = [
    { label: 'Gigabit', value: gig },
    { label: 'FTTP', value: b.fttp_pct },
    { label: '4G out', value: b.fourg_outdoor_pct },
    { label: '5G out', value: b.fiveg_outdoor_pct },
    { label: 'USO', value: 100 - uso, suffix: ' avail.' },
  ];

  return (
    <div className={`grid grid-cols-5 gap-1 ${className}`} data-testid="connectivity-compact">
      {items.map((it) => (
        <div key={it.label} className="flex flex-col items-center text-center bg-ink/[0.03] rounded-[3px] px-1 py-1.5">
          <div className="flex items-center gap-1 mb-0.5">
            <div className={`w-1.5 h-1.5 rounded-full ${dot(it.value)}`} />
            <span className="font-body text-[8.5px] text-ink/45 uppercase tracking-wider">{it.label}</span>
          </div>
          <span className="font-display text-[10px] font-extrabold text-ink leading-none">{fmtPct(it.value)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Full panel for BusinessProfile — labelled rows with icons + a small caption
 * explaining that these are baseline-augmented Ofcom figures.
 */
export default function ConnectivityProfile({ business, className = '' }) {
  const b = useConnectivityBaseline();
  const gig = business?.ofcom_gigabit_pct ?? b.gigabit_pct;
  const ufbb = business?.ofcom_ufbb_pct ?? b.ultrafast_pct;
  const sfbb = business?.ofcom_sfbb_pct;
  const speed = business?.download_speed;
  const broadbandType = business?.broadband_type;
  const uso = business?.ofcom_below_uso_pct ?? b.below_uso_pct;

  const rows = [
    {
      icon: Wifi,
      label: 'Gigabit-capable',
      value: fmtPct(gig),
      detail: 'Any tech (cable + fibre)',
      meta: business?.ofcom_gigabit_pct != null ? 'For this premises' : 'Scope baseline — Ofcom Jan 2026',
    },
    {
      icon: Wifi,
      label: 'Full Fibre (FTTP)',
      value: fmtPct(b.fttp_pct),
      detail: broadbandType ? `${broadbandType} · ${speed > 0 ? `${speed} Mbps` : 'speed n/a'}` : 'Direct fibre to premises',
      meta: 'Scope baseline — Ofcom Jan 2026',
    },
    {
      icon: Radio,
      label: 'Fixed Wireless Access',
      value: fmtPct(b.fwa_pct),
      detail: 'Decent broadband via FWA',
      meta: 'Scope baseline — Ofcom Jan 2026',
    },
    {
      icon: Wifi,
      label: 'Ultrafast (≥100 Mbps)',
      value: fmtPct(ufbb),
      detail: sfbb != null ? `Superfast: ${fmtPct(sfbb)}` : null,
      meta: business?.ofcom_ufbb_pct != null ? 'For this premises' : 'Scope baseline — Ofcom Jan 2026',
    },
    {
      icon: Smartphone,
      label: '4G outdoor',
      value: fmtPct(b.fourg_outdoor_pct),
      detail: 'All four MNOs',
      meta: 'Scope baseline — Ofcom Jan 2026',
    },
    {
      icon: Smartphone,
      label: '4G indoor',
      value: fmtPct(b.fourg_indoor_pct),
      detail: 'Geographic average',
      meta: 'Scope baseline — Ofcom Jan 2026',
    },
    {
      icon: Radio,
      label: '5G outdoor',
      value: fmtPct(b.fiveg_outdoor_pct),
      detail: 'Any operator',
      meta: 'Scope baseline — Ofcom Jan 2026',
    },
    {
      icon: ShieldCheck,
      label: 'Universal Service Obligation',
      value: '100% available',
      detail: `${fmtPct(uso)} below the USO floor`,
      meta: 'Every premises in the borough',
    },
  ];

  return (
    <div className={`rounded-[6px] border border-ink/10 bg-white p-4 sm:p-5 ${className}`} data-testid="connectivity-profile">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-display text-sm font-extrabold text-ink flex items-center gap-2">
            <Signal className="w-4 h-4 text-brand-orange" strokeWidth={1.5} />
            Connectivity Profile
          </h3>
          <p className="font-body text-[11px] text-ink/45 mt-0.5">
            Every premises in this borough has at least one form of broadband + mobile connectivity. Specifics for this premises are surfaced where available.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map((r, i) => {
          const Icon = r.icon;
          return (
            <div key={i} className="flex items-start gap-3 p-2.5 rounded-[4px] bg-ink/[0.025] border border-ink/[0.04]">
              <div className="w-7 h-7 rounded-[4px] bg-white flex items-center justify-center shrink-0">
                <Icon className="w-3.5 h-3.5 text-brand-orange" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-body text-[11px] text-ink/65 truncate">{r.label}</span>
                  <span className="font-display text-sm font-extrabold text-ink whitespace-nowrap">{r.value}</span>
                </div>
                {r.detail && <p className="font-body text-[10px] text-ink/45 mt-0.5 truncate">{r.detail}</p>}
                <p className="font-body text-[9px] text-ink/30 uppercase tracking-wider mt-0.5">{r.meta}</p>
              </div>
            </div>
          );
        })}
      </div>
      <p className="font-body text-[10px] text-ink/30 mt-3 italic">Source: {b.source || 'Ofcom Connected Nations, July 2025'}</p>
    </div>
  );
}
